/**
 * adminUtils.jsx — Componentes e utilitários compartilhados entre as abas do AdminPanel.
 * Sprint 6: Extração para reduzir AdminPanel.jsx de ~2700→~400 linhas.
 * Sprint 7: Formatadores consolidados — re-exporta de shared/formatters.js
 */
import PropTypes from "prop-types";

// ─── Re-export formatadores canônicos de shared/formatters.js ────────────────
// Evita duplicação: fonte única de verdade em formatters.js
// NOTA: import + re-export em vez de `export { ... } from` + import separado
// para evitar dual bindings que causam TDZ em Rollup production builds.
import { fmt, fmtP, pc, pb, fmtR, fmtRK } from "../shared/formatters.js";
export { fmt, fmtP, pc, pb, fmtR, fmtRK };

// fmtK: alias de fmtRK (trata valores < 1000 corretamente)
// Function declaration (not const) — hoisted, immune to TDZ in Rollup flat bundles.
export function fmtK(v) {
  return fmtRK(v);
}

// ─── Theme constants ────────────────────────────────────────────────────────
export const LIGHT = {
  navy: "#0B2447",
  navy2: "#19376D",
  gold: "#19376D",
  bg: "#EEF2F7",
  card: "#FFFFFF",
  text: "#1E293B",
  sub: "#475569",
  border: "#E2E8F0",
  muted: "#5F6B7A",
  row1: "#FFFFFF",
  row2: "#F8FAFC",
  // Brand tokens (synced with theme.js)
  accent: "#1D4ED8",
  accentHover: "#1E40AF",
  accentText: "#FFFFFF",
  surface: "#F8FAFC",
  // Semantic colors (WCAG AA on white)
  success: "#15803D",
  danger: "#DC2626",
  warning: "#B45309",
  info: "#2563EB",
  successBg: "#DCFCE7",
  dangerBg: "#FEE2E2",
  warningBg: "#FEF3C7",
  infoBg: "#DBEAFE",
};

export const DARK = {
  navy: "#0F172A",
  navy2: "#1E293B",
  gold: "#1E293B",
  bg: "#0F172A",
  card: "#1E293B",
  text: "#F6F3EE",
  sub: "#8E96A3",
  border: "#334155",
  muted: "#8293A6",
  row1: "#1E293B",
  row2: "#162032",
  // Brand tokens (synced with theme.js)
  accent: "#2563EB",
  accentHover: "#1D4ED8",
  accentText: "#FFFFFF",
  surface: "#162032",
  // Semantic colors (bright for dark bg)
  success: "#4ADE80",
  danger: "#F87171",
  warning: "#FBBF24",
  info: "#60A5FA",
  successBg: "rgba(74,222,128,0.12)",
  dangerBg: "rgba(248,113,113,0.12)",
  warningBg: "rgba(251,191,36,0.12)",
  infoBg: "rgba(96,165,250,0.12)",
};

export function fmtBytes(b) {
  if (!b) return "–";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

export function fmtDateStr(iso) {
  if (!iso) return "–";
  const d = new Date(iso);
  return (
    d.toLocaleDateString("pt-BR") +
    " " +
    d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })
  );
}

export function fileIcon(type) {
  if (!type) return "📄";
  if (type.includes("pdf")) return "📕";
  if (type.includes("sheet") || type.includes("excel") || type.includes("csv")) return "📗";
  if (type.includes("image")) return "🖼️";
  return "📄";
}

// ─── Shared input/form styles ───────────────────────────────────────────────
export function adminInputStyle(T) {
  return {
    width: "100%",
    padding: "8px 12px",
    borderRadius: 8,
    border: `1.5px solid ${T.border}`,
    fontSize: 13,
    outline: "none",
    background: T.bg,
    color: T.text,
    boxSizing: "border-box",
    transition: "border-color 0.15s, box-shadow 0.15s",
  };
}

export function adminInputFocusStyle(T) {
  return {
    borderColor: T.accent,
    boxShadow: `0 0 0 2px ${T.accent}30`,
  };
}

export function adminSelectStyle(T) {
  return { ...adminInputStyle(T), cursor: "pointer", appearance: "none" };
}

export function adminBtnStyle(T, variant = "default") {
  const base = {
    padding: "8px 16px",
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    transition: "background 0.15s, box-shadow 0.15s, opacity 0.15s",
    border: "none",
  };
  if (variant === "primary") {
    return { ...base, background: T.accent, color: "#fff", border: "none" };
  }
  if (variant === "ghost") {
    return { ...base, background: "transparent", color: T.text, border: `1px solid ${T.border}` };
  }
  return { ...base, background: T.surface || T.bg, color: T.text, border: `1px solid ${T.border}` };
}

// ─── UI Components ──────────────────────────────────────────────────────────
export function Badge({ text, color = "#19376D", bg }) {
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        padding: "2px 8px",
        borderRadius: 99,
        background: bg || `${color}20`,
        color,
        whiteSpace: "nowrap",
      }}
    >
      {text}
    </span>
  );
}

Badge.propTypes = {
  text: PropTypes.string.isRequired,
  color: PropTypes.string,
  bg: PropTypes.string,
};

export function Card({ children, style = {}, T }) {
  return (
    <div
      style={{
        background: T.card,
        borderRadius: 12,
        border: `1px solid ${T.border}`,
        boxShadow: `0 1px 4px rgba(0,0,0,${T === DARK ? 0.3 : 0.07}), 0 4px 16px rgba(0,0,0,${T === DARK ? 0.15 : 0.04})`,
        padding: 24,
        transition: "box-shadow 0.15s cubic-bezier(0.4,0,0.2,1)",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

Card.propTypes = {
  children: PropTypes.node,
  style: PropTypes.object,
  T: PropTypes.object,
};

export function STitle({ title, sub, T }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <h2 style={{ fontSize: 16, fontWeight: 700, color: T.text, margin: 0 }}>{title}</h2>
      {sub && <p style={{ fontSize: 11, color: T.muted, margin: "3px 0 0" }}>{sub}</p>}
    </div>
  );
}

STitle.propTypes = {
  title: PropTypes.string,
  sub: PropTypes.string,
  T: PropTypes.object,
};

export function AdminTabs({ tabs, active, onChange, T }) {
  return (
    <div
      style={{
        display: "flex",
        gap: 4,
        background: T.bg,
        padding: 4,
        borderRadius: 10,
        marginBottom: 18,
        flexWrap: "wrap",
      }}
    >
      {tabs.map((t) => (
        <button
          key={t}
          onClick={() => onChange(t)}
          style={{
            padding: "6px 14px",
            borderRadius: 8,
            border: "none",
            cursor: "pointer",
            fontSize: 12,
            fontWeight: 600,
            background: active === t ? T.card : "transparent",
            color: active === t ? T.text : T.sub,
            boxShadow: active === t ? `0 1px 4px rgba(0,0,0,0.1)` : "none",
          }}
        >
          {t}
        </button>
      ))}
    </div>
  );
}

AdminTabs.propTypes = {
  tabs: PropTypes.arrayOf(PropTypes.string).isRequired,
  active: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  T: PropTypes.object,
};

// ─── ADMIN_NAV: grouped sidebar navigation ──────────────────────────────────
export const ADMIN_NAV = [
  {
    key: "cockpit",
    label: "Cockpit",
    icon: "🎯",
    items: null, // top-level item, no children — Cockpit V2 (sem dependência de RPCs deferidas)
  },
  {
    key: "matriz",
    label: "Matriz analítica",
    icon: "📈",
    items: null, // top-level — Cross-clientes Sharpe/Alpha/Drift/Duration
  },
  {
    key: "tarefas",
    label: "Minhas tarefas",
    icon: "📋",
    items: null, // top-level — Personal Task Layer (Sprint 2-A)
  },
  {
    key: "home",
    label: "Painel de Controle",
    icon: "📊",
    items: null, // top-level item, no children
  },
  {
    group: "CLIENTES",
    icon: "👥",
    items: [
      { key: "Clientes", label: "Clientes", icon: "👤" },
      { key: "Portfolio", label: "Portfolio", icon: "💼" },
      { key: "Onboarding", label: "Onboarding", icon: "📋" },
      { key: "CRM", label: "CRM", icon: "💬" },
      { key: "Pipeline", label: "Pipeline", icon: "🚀" },
    ],
  },
  {
    group: "OPERAÇÕES",
    icon: "💼",
    items: [
      { key: "Importar Dados", label: "Importar Dados", icon: "📥" },
      { key: "Extratos", label: "Extratos", icon: "📄" },
      { key: "Snapshots", label: "Snapshots", icon: "📸" },
    ],
  },
  {
    group: "ANÁLISE",
    icon: "📈",
    items: [
      { key: "Métricas", label: "Métricas", icon: "📊" },
      { key: "Rebalanceamento", label: "Drift Dashboard", icon: "⚖️" },
      // "Motor Rebal." deprecated — substituído por /admin/clientes/:id/realocador (V2)
      // Acessível via tab Clientes → linha → botão 📊 Realocar OU header Tracker → Realocador
      { key: "Drift 2.0", label: "Drift Engine 2.0", icon: "🎚" },
      { key: "Análise Avançada", label: "Análise Avançada", icon: "🔬" },
      { key: "Simulador", label: "Simulador Alocação", icon: "📐" },
      { key: "Prep Review", label: "Prep Review", icon: "📝" },
    ],
  },
  {
    group: "FINANCEIRO",
    icon: "💰",
    items: [
      { key: "Fin. Dashboard", label: "Fin. Dashboard", icon: "📊" },
      { key: "Cobranças", label: "Cobranças", icon: "💳" },
      { key: "Revisões", label: "Revisões", icon: "📅" },
      { key: "Relatórios", label: "Relatórios", icon: "📬" },
      { key: "Report Factory", label: "Report Factory", icon: "✦" },
    ],
  },
  {
    group: "SISTEMA",
    icon: "⚙️",
    items: [
      { key: "Alertas", label: "Alertas", icon: "🔔" },
      { key: "Suitability", label: "Modelos Suitability", icon: "🎯" },
      { key: "Logs", label: "Logs", icon: "📜" },
      { key: "Monitor", label: "Monitor", icon: "🖥️" },
    ],
  },
];

// ─── AdminSidebar ───────────────────────────────────────────────────────────
import { useState as useSt, useEffect as useEff } from "react";
import { useIsMobile, useIsTablet } from "../shared/hooks.js";

/* Sidebar item with hover effect (avoids needing CSS :hover) */
function SideItem({
  isActive,
  gold,
  collapsed,
  onClick,
  label,
  icon,
  indent,
  fontSize = 13,
  fontWeight,
}) {
  const [hov, setHov] = useSt(false);
  const activeBg = indent ? "rgba(25,55,109,0.22)" : "rgba(25,55,109,0.28)";
  const hoverBg = "rgba(255,255,255,0.06)";
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: indent ? 8 : 10,
        padding: collapsed
          ? indent
            ? "7px 0"
            : "10px 0"
          : indent
            ? "7px 16px 7px 32px"
            : "10px 16px",
        justifyContent: collapsed ? "center" : "flex-start",
        background: isActive ? activeBg : hov ? hoverBg : "transparent",
        border: "none",
        borderLeft: isActive ? `3px solid ${gold}` : "3px solid transparent",
        color: isActive ? "#F0F2F5" : hov ? "rgba(255,255,255,0.92)" : "rgba(255,255,255,0.75)",
        cursor: "pointer",
        fontSize,
        fontWeight: isActive ? 700 : fontWeight || (indent ? 400 : 500),
        width: "100%",
        textAlign: "left",
        transition: "background 0.15s, color 0.15s",
        borderRadius: 0,
      }}
      title={collapsed ? label : undefined}
    >
      <span style={{ fontSize: indent ? 13 : 15, flexShrink: 0 }}>{icon}</span>
      {!collapsed && label}
    </button>
  );
}

SideItem.propTypes = {
  isActive: PropTypes.bool,
  gold: PropTypes.string,
  collapsed: PropTypes.bool,
  onClick: PropTypes.func,
  label: PropTypes.string,
  icon: PropTypes.string,
  indent: PropTypes.bool,
  fontSize: PropTypes.number,
  fontWeight: PropTypes.number,
};

export function AdminSidebar({ active, onChange, T: _T, collapsed, onToggleCollapse }) {
  const isMobile = useIsMobile(768); // < 768px: hide sidebar
  const isTablet = useIsTablet(1024); // < 1024px: auto-collapse
  const [openGroups, setOpenGroups] = useSt(() => {
    const initial = {};
    ADMIN_NAV.forEach((g) => {
      if (g.items) {
        const hasActive = g.items.some((it) => it.key === active);
        initial[g.group] = hasActive;
      }
    });
    return initial;
  });

  // Auto-collapse on tablet, auto-expand on desktop
  const [autoCollapsed, setAutoCollapsed] = useSt(isTablet);
  useEff(() => {
    setAutoCollapsed(isTablet);
  }, [isTablet]);

  const toggleGroup = (group) => {
    setOpenGroups((prev) => ({ ...prev, [group]: !prev[group] }));
  };

  const shouldCollapse = collapsed || autoCollapsed;
  const sideW = shouldCollapse ? 52 : 220;
  const gold = _T.accent;

  return (
    <nav
      data-admin-sidebar="true"
      style={{
        width: sideW,
        minHeight: "calc(100vh - 48px)",
        background: _T.navy,
        borderRight: `1px solid rgba(255,255,255,0.06)`,
        transition: "width 0.25s cubic-bezier(0.4,0,0.2,1)",
        display: isMobile ? "none" : "flex",
        flexDirection: "column",
        padding: "12px 0 8px",
        overflowX: "hidden",
        flexShrink: 0,
        position: isMobile ? "fixed" : "relative",
        left: 0,
        top: 0,
        height: isMobile ? "100vh" : "auto",
        zIndex: 50,
      }}
    >
      {/* Collapse toggle */}
      <button
        onClick={onToggleCollapse}
        style={{
          background: "transparent",
          border: "none",
          color: "rgba(255,255,255,0.7)",
          cursor: "pointer",
          padding: "4px 14px",
          textAlign: shouldCollapse ? "center" : "right",
          fontSize: 14,
          marginBottom: 8,
          transition: "color 0.15s",
        }}
        aria-label={shouldCollapse ? "Expandir menu" : "Recolher menu"}
      >
        {shouldCollapse ? "☰" : "◀"}
      </button>

      {ADMIN_NAV.map((section) => {
        // Top-level item (Painel de Controle)
        if (!section.items && section.key) {
          return (
            <div key={section.key} style={{ marginBottom: 8 }}>
              <SideItem
                isActive={active === section.key}
                gold={gold}
                collapsed={shouldCollapse}
                onClick={() => onChange(section.key)}
                label={section.label}
                icon={section.icon}
                indent={false}
              />
            </div>
          );
        }

        // Group with children
        if (!section.items) return null;
        const isOpen = openGroups[section.group] ?? false;
        const groupHasActive = section.items.some((it) => it.key === active);

        return (
          <div key={section.group} style={{ marginBottom: 4, marginTop: 4 }}>
            {/* Group header */}
            <button
              onClick={() => toggleGroup(section.group)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: shouldCollapse ? "6px 0" : "6px 16px",
                justifyContent: shouldCollapse ? "center" : "flex-start",
                background: "transparent",
                border: "none",
                color: groupHasActive ? "rgba(255,255,255,0.95)" : "rgba(255,255,255,0.5)",
                cursor: "pointer",
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: 1,
                textTransform: "uppercase",
                width: "100%",
                textAlign: "left",
                transition: "color 0.15s",
              }}
              title={shouldCollapse ? section.group : undefined}
            >
              <span style={{ fontSize: 11 }}>{section.icon}</span>
              {!shouldCollapse && (
                <>
                  <span style={{ flex: 1 }}>{section.group}</span>
                  <span
                    style={{
                      fontSize: 8,
                      transition: "transform 0.2s cubic-bezier(0.4,0,0.2,1)",
                      transform: isOpen ? "rotate(90deg)" : "rotate(0deg)",
                      opacity: 0.6,
                    }}
                  >
                    ▶
                  </span>
                </>
              )}
            </button>

            {/* Group items */}
            {(isOpen || shouldCollapse) &&
              section.items.map((item) => (
                <SideItem
                  key={item.key}
                  isActive={active === item.key}
                  gold={gold}
                  collapsed={shouldCollapse}
                  onClick={() => onChange(item.key)}
                  label={item.label}
                  icon={item.icon}
                  indent
                  fontSize={12}
                />
              ))}
          </div>
        );
      })}
    </nav>
  );
}

AdminSidebar.propTypes = {
  active: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  T: PropTypes.object,
  collapsed: PropTypes.bool,
  onToggleCollapse: PropTypes.func,
};
